// All public copy externalized (English). Keeps future localization cheap; no hardcoded strings in components.

export const IMAGES = {
  hero: "/img/hero.jpg",
  portDusk: "/img/port-dusk.jpg",
  freight: "/img/freight.jpg",
  warehouse: "/img/warehouse.jpg",
  qualityWorker: "/img/quality-worker.jpg",
  pharmaLab: "/img/pharma-lab.jpg",
  perfumery: "/img/perfumery.jpg",
  cosmetics: "/img/cosmetics.jpg",
  botanical: "/img/botanical.jpg",
  spaLeaf: "/img/spa-leaf.jpg",
  // Neutral professional B2B imagery (no national flags / no governmental implication)
  docsReview: "/img/docs-review.jpg",
  contractSign: "/img/contract-sign.jpg",
  officeDiscuss: "/img/office-discuss.jpg",
  consumerHealth: "/img/consumer-health.jpg",
};

export const CONTACTS = {
  general: "info@azekaglobal.com",
  trade: "trade@azekaglobal.com",
  location: "Dubai, United Arab Emirates",
  entity: "Azeka Global Trade FZCO",
};

// Per-page SEO (full, already-composed titles + factual descriptions). No keyword stuffing.
export const SEO = {
  home: { t: "Azeka Global | International B2B Sourcing & Trade", d: "Azeka Global (AZEKA GLOBAL TRADE FZCO) is a Dubai-based B2B sourcing and trading company across pharmaceutical, consumer-health, perfumery and personal-care goods." },
  about: { t: "About Azeka Global | AZEKA GLOBAL TRADE FZCO", d: "About Azeka Global — a Dubai-based international sourcing and trading company connecting qualified suppliers with licensed buyers across regulated and consumer-goods markets." },
  whatWeDo: { t: "International Sourcing & Trade | Azeka Global", d: "How Azeka Global coordinates structured sourcing, cross-border trading and market-access across regulated and consumer goods." },
  productAreas: { t: "Product Areas | Azeka Global", d: "Azeka Global's focus categories: pharmaceutical & medical goods, consumer health & para-pharmaceuticals, perfumeries, and cosmetics & personal care." },
  ourBrands: { t: "Our Brands & Azeka Health | Azeka Global", d: "Azeka Global develops and commercialises health and wellbeing brands through Azeka Health, the dedicated platform of AZEKA GLOBAL TRADE FZCO." },
  forSuppliers: { t: "Supplier Partnerships | Azeka Global", d: "Partner with Azeka Global — structured international trade partnerships for manufacturers and authorised suppliers of regulated and consumer goods." },
  forBuyers: { t: "International Sourcing for B2B Buyers | Azeka Global", d: "Source through Azeka Global's qualified international supply channels, with clear commercial documentation and coordinated cross-border supply." },
  quality: { t: "Quality & Compliance | Azeka Global", d: "Azeka Global's approach to supplier due diligence, documentation and careful handling in regulated and consumer-goods trade." },
  contact: { t: "Contact Azeka Global | Dubai, UAE", d: "Contact Azeka Global (AZEKA GLOBAL TRADE FZCO), Dubai, United Arab Emirates — general enquiries and trade, sourcing and supplier enquiries." },
  health: { t: "Azeka Health | Health & Wellbeing Brands", d: "Azeka Health is the dedicated platform for health and wellbeing brands owned or controlled by AZEKA GLOBAL TRADE FZCO, part of its commercial brand activities. Portfolio coming soon." },
};

// Insights is intentionally hidden until approved content exists (Phase 1B).
export const FEATURES = {
  insights: false,
};

export const NAV = [
  { label: "About", to: "/about" },
  { label: "What We Do", to: "/what-we-do" },
  { label: "Product Areas", to: "/product-areas" },
  { label: "Our Brands", to: "/our-brands" },
  { label: "For Suppliers", to: "/for-suppliers" },
  { label: "For Buyers", to: "/for-buyers" },
  { label: "Quality & Compliance", to: "/quality-compliance" },
];

export const PRODUCT_AREAS = [
  {
    id: "pharma",
    title: "Pharmaceutical & Medical Goods",
    summary:
      "Sourcing and trade of pharmaceutical products and medical goods through qualified, appropriately authorised channels, structured around the transaction and product requirements that apply to each market.",
    image: IMAGES.pharmaLab,
  },
  {
    id: "consumer-health",
    title: "Consumer Health & Para-pharmaceuticals",
    summary:
      "Over-the-counter wellness, supplements and para-pharmaceutical lines, sourced with the applicable regulatory and labelling requirements of the intended market in mind.",
    image: IMAGES.consumerHealth,
  },
  {
    id: "perfumeries",
    title: "Perfumeries",
    summary:
      "Fine fragrance and perfumery sourcing across established houses and niche producers, with careful attention to authenticity and provenance.",
    image: IMAGES.perfumery,
  },
  {
    id: "cosmetics",
    title: "Cosmetics & Personal Care",
    summary:
      "Skincare, colour cosmetics and personal care ranges supplied to distributors and retailers, supported by coordinated supply and commercial documentation.",
    image: IMAGES.cosmetics,
  },
];

export const CAPABILITIES = [
  {
    icon: "Search",
    title: "Sourcing & Procurement",
    body: "We identify and qualify supply through appropriately authorised manufacturers and channels, and structure procurement terms designed to hold up over the long term.",
  },
  {
    icon: "Repeat",
    title: "Cross-Border Trading",
    body: "We connect qualified supply with qualified business demand, structuring transparent international transactions between appropriately authorised parties.",
  },
  {
    icon: "ShieldCheck",
    title: "Market-Access Coordination",
    body: "We coordinate the documentation and requirements involved in moving products between markets, working with authorised parties where regulated functions apply.",
  },
  {
    icon: "Ship",
    title: "Logistics & Trade Coordination",
    body: "We coordinate freight, storage and delivery with specialist partners so goods move safely and on schedule.",
  },
];

export const HOME = {
  hero: {
    eyebrow: "International B2B Trade",
    title: "International Trade. Structured Sourcing. Reliable Execution.",
    subtitle:
      "A Dubai-based B2B trading company connecting qualified suppliers with licensed buyers and commercial partners across pharmaceutical, consumer-health, beauty and related international markets.",
    primary: { label: "Make an Enquiry", to: "/contact" },
    secondary: { label: "Explore What We Do", to: "/what-we-do" },
  },
  credibility: [
    "Dubai, UAE",
    "International B2B Trade",
    "Regulated & Consumer Markets",
    "Structured Procurement",
  ],
  intro: {
    eyebrow: "Who we are",
    title: "A dependable partner between supply and demand",
    body: "We operate at the intersection of manufacturers, distributors, wholesalers and importers — bringing structure, diligence and accountability to cross-border trade in regulated and consumer goods. Our focus is straightforward: the right products, sourced through qualified channels and supported by appropriate documentation for their intended markets.",
  },
  capabilities: {
    eyebrow: "What we do",
    title: "Capabilities built around trust and traceability",
    body: "We coordinate the commercial stages that make international trade reliable — from initial enquiry through to coordinated delivery.",
  },
  productAreas: {
    eyebrow: "Product areas",
    title: "Focused expertise across four categories",
    body: "We concentrate on sectors where provenance, documentation and quality matter most.",
  },
  brands: {
    eyebrow: "Our brands",
    title: "Introducing Azeka Health",
    body: "Azeka Health is the dedicated home of Azeka-owned or controlled health and wellbeing brands, carrying our Pure Wellbeing identity. The portfolio is being prepared with qualified manufacturing and market partners.",
    status: "Portfolio Coming Soon",
    cta: { label: "Visit Azeka Health", to: "https://azekahealth.com" },
  },
  audience: {
    eyebrow: "How we work with you",
    suppliers: {
      title: "For Suppliers",
      body: "Build structured international trade partnerships with a Dubai-based partner focused on provenance, documentation and long-term relationships. Share your capabilities and we will explore where they fit.",
      cta: { label: "Partner with us", to: "/for-suppliers" },
    },
    buyers: {
      title: "For Buyers",
      body: "Source through qualified international supply channels, with clear commercial documentation and coordinated cross-border supply. Tell us what you need and we will respond.",
      cta: { label: "Source with us", to: "/for-buyers" },
    },
  },
  quality: {
    eyebrow: "Quality & compliance",
    title: "Diligence is the foundation, not an add-on",
    body: "We apply proportionate checks to counterparties, relevant authorisations and product-source information as part of transaction review. Documentation requirements depend on the product, the parties and the intended destination, and regulated functions are carried out by appropriately authorised entities.",
    points: [
      "Supplier and product-source due diligence",
      "Documentation appropriate to the product and destination",
      "Careful handling coordinated with specialist partners",
      "Transparent, accountable transaction records",
    ],
    cta: { label: "Our approach to quality", to: "/quality-compliance" },
  },
  cta: {
    title: "Let's talk about your next requirement",
    body: "Whether you are sourcing, supplying or exploring a new market, our team is ready to help.",
    primary: { label: "Make an Enquiry", to: "/contact" },
  },
};

export const PAGES = {
  about: {
    eyebrow: "About Azeka Global",
    title: "International trade built on structure and accountability",
    lead: "Azeka Global Trade FZCO is a Dubai-based international sourcing and trading company connecting qualified suppliers with licensed buyers and commercial partners across regulated and consumer-goods markets.",
    sections: [
      {
        heading: "Our purpose",
        body: "We exist to make cross-border trade in sensitive product categories more reliable. That means sourcing through qualified channels, disciplined attention to documentation, and coordinated logistics — so buyers receive what they expect and suppliers reach new markets with confidence.",
      },
      {
        heading: "How we operate",
        body: "Based in Dubai, United Arab Emirates, we sit at a natural crossroads of international trade. We work with appropriately authorised manufacturers and channels, maintain clear commercial and transaction records within our role, and treat every transaction as the start of a long-term relationship rather than a one-off deal.",
      },
      {
        heading: "What guides us",
        body: "Integrity, transparency and accountability. We prioritise provenance, appropriate documentation and applicable compliance requirements at every stage we coordinate — a deliberately focused approach designed to support responsible trade in regulated and consumer goods.",
      },
    ],
  },
  whatWeDo: {
    eyebrow: "What we do",
    title: "Structured sourcing, trading and market-access coordination",
    lead: "We coordinate the key commercial stages of moving products across borders — sourcing through qualified channels, structuring transactions, coordinating market access and logistics arrangements. Where regulated functions are required, they are carried out by appropriately authorised parties.",
  },
  productAreas: {
    eyebrow: "Product areas",
    title: "Focused expertise across four categories",
    lead: "We concentrate on categories where provenance, documentation and quality matter most — pharmaceutical and medical goods, consumer health and para-pharmaceuticals, perfumeries, and cosmetics and personal care. In each, Azeka sources and trades through qualified channels rather than storing, dispensing or handling goods directly.",
  },
  ourBrands: {
    eyebrow: "Our brands",
    title: "Building brands for responsible international growth",
    lead: "AZEKA GLOBAL TRADE FZCO develops and commercialises selected health, wellbeing and related brands through qualified manufacturing and market partners. Azeka Health is the dedicated platform for this growing portfolio.",
  },
  forSuppliers: {
    eyebrow: "For suppliers",
    title: "Build structured international trade partnerships",
    lead: "If you manufacture or are authorised to supply pharmaceutical, consumer-health, perfumery or personal care products, we would like to understand your capabilities. Share your details below and our trade team will review them.",
  },
  forBuyers: {
    eyebrow: "For buyers",
    title: "Source through qualified international supply channels",
    lead: "Azeka works with qualified business buyers seeking structured sourcing, clear commercial documentation and coordinated cross-border supply. Tell us what you are looking for and we will respond.",
  },
  quality: {
    eyebrow: "Quality & compliance",
    title: "Compliance is the foundation, not the afterthought",
    lead: "Trade in regulated and consumer goods works best when diligence is built in from the start. Here is how we keep integrity and traceability at the centre of the transactions and activities we coordinate.",
    sections: [
      {
        heading: "Supplier & Source Due Diligence",
        body: "We apply proportionate checks to counterparties, relevant authorisations and product-source information as part of transaction review.",
      },
      {
        heading: "Documentation & regulation",
        body: "Documentation requirements depend on the product, the parties and the intended destination. Regulated responsibilities are performed by appropriately authorised entities.",
      },
      {
        heading: "Careful handling",
        body: "We coordinate storage and freight with specialist partners who respect the handling requirements of sensitive products.",
      },
      {
        heading: "Accountability",
        body: "We maintain appropriate commercial and transaction records within Azeka's role, keeping our activities transparent and traceable.",
      },
    ],
  },
  contact: {
    eyebrow: "Contact",
    title: "Make an enquiry",
    lead: "Tell us what you need and we will route your enquiry to the right team. General questions reach our main office; trade, sourcing and supplier enquiries reach our trade desk directly.",
  },
};

export const HEALTH = {
  status: "Portfolio Coming Soon",
  title: "Pure Wellbeing, in the making",
  body: "Azeka Health is the dedicated home of Azeka-owned or controlled health and wellbeing brands. Our portfolio is being prepared with the same commitment to quality, provenance and integrity that defines Azeka.",
  note: "For partnership or trade enquiries in the meantime, our team is ready to help.",
  cta: { label: "Enquire with Azeka Global", to: "/contact" },
  back: { label: "Return to Azeka Global", to: "/" },
};

export const FOOTER = {
  tagline: "A Dubai-based international B2B sourcing and trading company, coordinating cross-border transactions through qualified and appropriately authorised counterparties.",
  columns: [
    {
      title: "Company",
      links: [
        { label: "About", to: "/about" },
        { label: "What We Do", to: "/what-we-do" },
        { label: "Our Brands", to: "/our-brands" },
        { label: "Quality & Compliance", to: "/quality-compliance" },
      ],
    },
    {
      title: "Trade",
      links: [
        { label: "Product Areas", to: "/product-areas" },
        { label: "For Suppliers", to: "/for-suppliers" },
        { label: "For Buyers", to: "/for-buyers" },
        { label: "Contact", to: "/contact" },
      ],
    },
  ],
  legal: [
    { label: "Privacy", to: "/privacy" },
    { label: "Cookies", to: "/cookies" },
    { label: "Terms of Use", to: "/terms" },
    { label: "Regulated Products Disclaimer", to: "/regulated-products-disclaimer" },
  ],
  licence: "AZEKA GLOBAL TRADE FZCO · Trade Licence 50717 · Registration RNo-03390",
};
